import { useEffect, useState } from "react";
import { getRinkelStatus, getCallQueue, linkCall, ignoreCall, getLeads, registerRinkelWebhooks, getRinkelNumbers, saveRinkelSettings, getRinkelAgents, updateUser } from "@/lib/apiClient";
import { Copy, RefreshCw, Link2, EyeOff, PhoneIncoming, PhoneOutgoing, Loader2, CheckCircle2, XCircle, Webhook } from "lucide-react";
import { toast } from "sonner";
import { CAUSE_LABEL, fmtDateTime } from "@/components/CallHistory";

const EVENTS = [
  { id: "outgoingCall", label: "Outgoing call (uitgaand)" },
  { id: "incomingCall", label: "Incoming call (inkomend)" },
  { id: "callStart", label: "Call start (opgenomen)" },
  { id: "callEnd", label: "Call end (beëindigd + opname)" },
];

export function rinkelBadge(s) {
  if (!s) return { label: "Controleren…", cls: "bg-[#EDF1F5] text-[#5B6675]" };
  if (!s.configured) return { label: "Rinkel niet ingesteld", cls: "bg-[#EDF1F5] text-[#5B6675]" };
  if (s.api_ok) return { label: "Rinkel gekoppeld", cls: "bg-[#16BFBF] text-white" };
  if (s.api_status === 401) return { label: "Rinkel-key ongeldig", cls: "bg-[#F3DEDD] text-[#B0413E]" };
  if (s.api_status === 403) return { label: "Rinkel-abonnement zonder API", cls: "bg-[#F3DEDD] text-[#B0413E]" };
  return { label: "Rinkel niet bereikbaar", cls: "bg-[#F3DEDD] text-[#B0413E]" };
}

export function RinkelBadge({ status }) {
  const b = rinkelBadge(status);
  return <span data-testid="rinkel-status-badge" className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${b.cls}`}><span className="h-1.5 w-1.5 rounded-full bg-current" />{b.label}</span>;
}

function StatusCard({ status, onRefresh }) {
  const rows = [
    ["API-key (RINKEL_API_KEY)", status?.configured ? "Ingesteld" : "Ontbreekt"],
    ["Verbinding met Rinkel API", status?.api_ok ? "OK" : status?.api_status ? `Mislukt (${status.api_status})` : "—"],
    ["Webhookgeheim (RINKEL_WEBHOOK_SECRET)", status?.webhook_secret_configured ? "Ingesteld" : "Ontbreekt"],
    ["Laatste webhook ontvangen", fmtDateTime(status?.last_webhook_at)],
    ["Gesprekken in koppelwachtrij", status?.queue_count ?? "—"],
  ];
  return (
    <div className="rounded-2xl border border-[#E2E7EC] bg-white p-6" data-testid="rinkel-status-card">
      <div className="flex items-center gap-3 mb-4">
        <h3 className="font-heading font-semibold text-lg text-[#16202B]">Rinkel-koppeling</h3>
        <RinkelBadge status={status} />
        <button data-testid="rinkel-refresh" onClick={onRefresh} className="ml-auto p-2 rounded-lg hover:bg-[#EDF1F5] text-[#5B6675]"><RefreshCw className="h-4 w-4" /></button>
      </div>
      <dl className="divide-y divide-[#E2E7EC]">
        {rows.map(([k, v]) => <div key={k} className="flex justify-between py-2 text-sm"><dt className="text-[#5B6675]">{k}</dt><dd data-testid={`rinkel-row-${k.split(" ")[0].toLowerCase()}`} className="font-medium text-[#16202B]">{v}</dd></div>)}
      </dl>
    </div>
  );
}

function OutboundNumber({ onSaved }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState("");
  useEffect(() => { getRinkelNumbers().then((d) => { setData(d); setSel(d.selected_id || ""); }).catch(() => setData({ numbers: [] })); }, []);
  const save = async () => {
    try { await saveRinkelSettings({ outbound_number_id: sel || null }); toast.success("Uitgaand nummer opgeslagen"); onSaved(); } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
  };
  return (
    <div className="rounded-2xl border border-[#E2E7EC] bg-white p-6" data-testid="rinkel-outbound">
      <h3 className="font-heading font-semibold text-lg text-[#16202B] mb-1">Uitgaand nummer (afzender)</h3>
      <p className="text-sm text-[#5B6675] mb-3">Met dit nummer bellen alle medewerkers vanuit het CRM. Elke medewerker moet in MyRinkel toegang hebben tot dit nummer.</p>
      <div className="flex flex-wrap items-center gap-2">
        <select data-testid="rinkel-outbound-select" value={sel} onChange={(e) => setSel(e.target.value)} className="fe-input w-80">
          <option value="">Automatisch (eerste nummer van de medewerker)</option>
          {(data?.numbers || []).map((n) => <option key={n.id} value={n.id}>{n.localized || n.number}{n.label ? ` — ${n.label}` : ""}</option>)}
        </select>
        <button data-testid="rinkel-outbound-save" onClick={save} className="px-4 py-2.5 rounded-xl bg-[#001C51] text-white text-sm font-medium hover:bg-[#0A337A]">Opslaan</button>
      </div>
    </div>
  );
}

const METHOD_LABEL = { webphone: "Rinkel Webphone (Click To Dial)", app: "Rinkel-app (via Rinkel-API)", none: "Niet mogelijk" };

function Agents({ reloadKey }) {
  const [data, setData] = useState(null);
  const load = () => getRinkelAgents().then(setData).catch(() => setData({ agents: [] }));
  useEffect(() => { setData(null); load(); }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const setMethod = async (a, v) => {
    try { await updateUser(a.id, { rinkel_call_method: v }); toast.success("Belmethode opgeslagen"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
  };
  const Ok = ({ ok, testid, warn }) => (ok ? <CheckCircle2 data-testid={testid} className="h-4 w-4 text-[#16BFBF]" /> : <XCircle data-testid={testid} className={`h-4 w-4 ${warn ? "text-[#B7791F]" : "text-[#B0413E]"}`} />);
  return (
    <div className="rounded-2xl border border-[#E2E7EC] bg-white p-6" data-testid="rinkel-agents">
      <h3 className="font-heading font-semibold text-lg text-[#16202B] mb-1">Telefoniecontrole per medewerker</h3>
      <p className="text-sm text-[#5B6675] mb-3">Live uit Rinkel opgehaald.{data?.outbound_number ? ` Uitgaand nummer: ${data.outbound_number}.` : ""} Een ontbrekend app-toestel is géén probleem voor bellen via de Webphone; dat is alleen nodig voor bellen via de Rinkel-app.</p>
      {!data ? <p className="text-sm text-[#5B6675]">Laden…</p> : (
        <div className="overflow-x-auto fe-scroll">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[#5B6675] text-xs uppercase tracking-wide"><th className="py-1.5">Medewerker</th><th>Rinkel-account</th><th>Gekoppeld</th><th>Nummer actief</th><th>App-toestel</th><th>Belmethode</th></tr></thead>
            <tbody>{data.agents.map((a) => (
              <tr key={a.id} data-testid={`agent-${a.id}`} className="border-t border-[#E2E7EC] align-top">
                <td className="py-2.5 font-medium text-[#16202B]">{a.name}<span className="text-xs text-[#8994A3] ml-1">({a.role})</span>
                  {a.steps?.length > 0 && <ul className="mt-1 space-y-0.5 font-normal" data-testid={`agent-steps-${a.id}`}>{a.steps.map((st, i) => <li key={i} className="text-xs text-[#B7791F]">• {st}</li>)}</ul>}
                </td>
                <td className="py-2.5 text-[#5B6675]">{a.rinkel_name ? <>{a.rinkel_name}<span className="block text-xs text-[#8994A3]">{a.rinkel_email}</span></> : "—"}{a.shared_rinkel && <span className="block text-xs text-[#B7791F]" data-testid={`agent-shared-${a.id}`}>gedeeld met andere CRM-gebruiker</span>}</td>
                <td className="py-2.5"><Ok ok={a.linked} testid={`agent-linked-${a.id}`} /></td>
                <td className="py-2.5"><Ok ok={a.number_access} testid={`agent-number-${a.id}`} /></td>
                <td className="py-2.5"><Ok ok={a.has_device} warn testid={`agent-device-${a.id}`} /></td>
                <td className="py-2.5">
                  <p className="text-xs font-semibold text-[#001C51]" data-testid={`agent-method-${a.id}`}>{METHOD_LABEL[a.effective_method]}</p>
                  {a.linked && (
                    <select data-testid={`agent-method-select-${a.id}`} value={a.call_method} onChange={(e) => setMethod(a, e.target.value)} className="fe-input py-1 text-xs mt-1 w-44">
                      <option value="auto">Automatisch</option><option value="webphone">Altijd Webphone</option><option value="app">Rinkel-app</option>
                    </select>
                  )}
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function WebhookRegister({ status, onDone }) {
  const [busy, setBusy] = useState(null);
  const [res, setRes] = useState(null);
  const origin = window.location.origin;
  const run = async (testOnly) => {
    if (!testOnly && !window.confirm(`Webhooks bij Rinkel aanmelden voor ${origin}?\n\nAlle Rinkel-gesprekken worden daarna naar deze omgeving gestuurd. Doe dit in je live CRM.`)) return;
    setBusy(testOnly ? "test" : "register"); setRes(null);
    try { const r = await registerRinkelWebhooks(testOnly); setRes(r); onDone(); toast.success(testOnly ? "Test afgerond" : "Aanmelden afgerond"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Mislukt"); }
    finally { setBusy(null); }
  };
  const byEvent = Object.fromEntries((status?.webhooks || []).map((w) => [w.event, w]));
  const Ok = ({ ok }) => (ok ? <CheckCircle2 className="h-4 w-4 text-[#16BFBF]" /> : <XCircle className="h-4 w-4 text-[#B0413E]" />);
  return (
    <div className="rounded-2xl border border-[#E2E7EC] bg-white p-6" data-testid="rinkel-webhook-register">
      <h3 className="font-heading font-semibold text-lg text-[#16202B] mb-1 flex items-center gap-2"><Webhook className="h-5 w-5 text-[#16BFBF]" /> Webhooks automatisch aanmelden</h3>
      <p className="text-sm text-[#5B6675] mb-4">Deze omgeving: <b className="text-[#001C51]" data-testid="rinkel-origin">{origin}</b>. De backend meldt zichzelf aan bij Rinkel met het eigen webhookgeheim — dat geheim komt nooit in je browser.</p>
      <div className="space-y-1.5 mb-4">
        {EVENTS.map((e) => {
          const w = byEvent[e.id];
          return (
            <div key={e.id} data-testid={`rinkel-sub-${e.id}`} className="flex items-center gap-2 text-sm">
              <span className="w-56 text-[#16202B]">{e.label}</span>
              {!w ? <span className="text-[#5B6675]">Niet aangemeld</span>
                : w.points_here ? <span className="text-[#0C6E6E] font-medium">{w.active ? "Actief" : "Inactief"} · wijst naar deze omgeving</span>
                : <span className="text-[#B7791F] font-medium">Aangemeld, maar wijst naar een andere URL</span>}
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-2">
        <button data-testid="rinkel-webhook-test" disabled={!!busy || !status?.api_ok} onClick={() => run(true)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#E2E7EC] text-[#001C51] text-sm font-medium hover:bg-[#EDF1F5] disabled:opacity-50">
          {busy === "test" && <Loader2 className="h-4 w-4 animate-spin" />} Verbinding testen
        </button>
        <button data-testid="rinkel-webhook-register-btn" disabled={!!busy || !status?.api_ok || !status?.webhook_secret_configured} onClick={() => run(false)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#001C51] text-white text-sm font-medium hover:bg-[#0A337A] disabled:opacity-50">
          {busy === "register" && <Loader2 className="h-4 w-4 animate-spin" />} Webhooks aanmelden bij Rinkel
        </button>
      </div>
      {res && (
        <table className="w-full text-sm mt-4" data-testid="rinkel-register-results">
          <thead><tr className="text-left text-[#5B6675]"><th className="py-1.5">Event</th><th>Rinkel-test</th><th>Ontvangen</th>{!res.test_only && <th>Aangemeld</th>}<th>Melding</th></tr></thead>
          <tbody>{res.results.map((r) => (
            <tr key={r.event} data-testid={`rinkel-result-${r.event}`} className="border-t border-[#E2E7EC]">
              <td className="py-1.5 font-medium">{r.event}</td><td><Ok ok={r.test_ok} /></td><td><Ok ok={r.test_received} /></td>
              {!res.test_only && <td><Ok ok={r.subscribed} /></td>}<td className="text-xs text-[#B0413E]">{r.error || ""}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </div>
  );
}

function WebhookInstructions() {
  const base = `${window.location.origin}/api/rinkel/webhooks`;
  const copy = async (v) => { try { await navigator.clipboard.writeText(v); toast.success("URL gekopieerd"); } catch { toast.error("Kopiëren mislukt"); } };
  return (
    <div className="rounded-2xl border border-[#E2E7EC] bg-white p-6" data-testid="rinkel-webhook-instructions">
      <h3 className="font-heading font-semibold text-lg text-[#16202B] mb-2">Of handmatig instellen in My Rinkel</h3>
      <ol className="list-decimal ml-5 text-sm text-[#16202B] space-y-1 mb-4">
        <li>Log in op <a className="text-[#001C51] underline" href="https://my.rinkel.com/" target="_blank" rel="noreferrer">my.rinkel.com</a> → Webphone → Instellingen (tandwiel) → Integraties.</li>
        <li>Klik per event hieronder, plak de URL en vervang <code className="bg-[#EDF1F5] px-1 rounded">JOUW_WEBHOOKGEHEIM</code> door de waarde van <code className="bg-[#EDF1F5] px-1 rounded">RINKEL_WEBHOOK_SECRET</code> uit het Secrets-paneel.</li>
        <li>Kies Content-Type <b>application/json</b>, klik op Test (groen vinkje) en daarna op Opslaan.</li>
      </ol>
      <div className="space-y-2">
        {EVENTS.map((e) => {
          const url = `${base}/${e.id}?token=JOUW_WEBHOOKGEHEIM`;
          return (
            <div key={e.id} className="flex items-center gap-2 rounded-xl border border-[#E2E7EC] bg-[#F6F9FB] px-3 py-2">
              <div className="min-w-0">
                <p className="text-xs font-semibold text-[#001C51]">{e.label}</p>
                <p className="text-xs text-[#5B6675] font-mono truncate" data-testid={`webhook-url-${e.id}`}>{url}</p>
              </div>
              <button data-testid={`copy-webhook-${e.id}`} onClick={() => copy(url)} className="ml-auto p-2 rounded-lg hover:bg-[#EDF1F5] text-[#001C51]"><Copy className="h-4 w-4" /></button>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-[#5B6675] mt-3">Let op: webhooks vereisen het Rinkel Expert-abonnement. Koppel medewerkers via hun Rinkel-gebruikers-ID onder Teams & gebruikers.</p>
    </div>
  );
}

function QueueRow({ c, leads, onDone }) {
  const [leadId, setLeadId] = useState(c.candidates?.[0]?.id || "");
  const options = c.candidates?.length ? c.candidates : leads;
  const doLink = async () => {
    if (!leadId) return toast.error("Kies een lead");
    try { await linkCall(c.id, leadId); toast.success("Gesprek gekoppeld"); onDone(); } catch (e) { toast.error(e?.response?.data?.detail || "Koppelen mislukt"); }
  };
  const doIgnore = async () => { await ignoreCall(c.id); toast.success("Genegeerd"); onDone(); };
  const In = c.direction === "inbound";
  return (
    <div data-testid={`queue-row-${c.id}`} className="rounded-xl border border-[#E2E7EC] bg-white p-3 flex flex-wrap items-center gap-3">
      {In ? <PhoneIncoming className="h-4 w-4 text-[#0C6E6E]" /> : <PhoneOutgoing className="h-4 w-4 text-[#001C51]" />}
      <div className="min-w-[180px]">
        <p className="text-sm font-medium text-[#16202B]">{c.external_number || "Anoniem nummer"}</p>
        <p className="text-xs text-[#5B6675]">{fmtDateTime(c.started_at || c.created_at)} · {CAUSE_LABEL[c.cause] || c.status || "—"} · {c.match_status === "multiple" ? `${c.candidates.length} mogelijke leads` : "geen lead gevonden"}</p>
      </div>
      <select data-testid={`queue-lead-select-${c.id}`} value={leadId} onChange={(e) => setLeadId(e.target.value)} className="fe-input w-56 ml-auto">
        <option value="">Kies lead…</option>
        {options.map((l) => <option key={l.id} value={l.id}>{l.name}{l.phone ? ` — ${l.phone}` : ""}</option>)}
      </select>
      <button data-testid={`queue-link-${c.id}`} onClick={doLink} className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-xl bg-[#001C51] text-white hover:bg-[#0A337A]"><Link2 className="h-4 w-4" /> Koppel</button>
      <button data-testid={`queue-ignore-${c.id}`} onClick={doIgnore} title="Negeren" className="p-2 rounded-xl border border-[#E2E7EC] text-[#5B6675] hover:bg-[#EDF1F5]"><EyeOff className="h-4 w-4" /></button>
    </div>
  );
}

function CallQueue({ onChanged }) {
  const [queue, setQueue] = useState([]);
  const [leads, setLeads] = useState([]);
  const load = () => { getCallQueue().then(setQueue).catch(() => {}); onChanged && onChanged(); };
  useEffect(() => { getCallQueue().then(setQueue).catch(() => {}); getLeads().then(setLeads).catch(() => {}); }, []);
  return (
    <div className="rounded-2xl border border-[#E2E7EC] bg-white p-6" data-testid="call-queue">
      <h3 className="font-heading font-semibold text-lg text-[#16202B] mb-1">Koppelwachtrij</h3>
      <p className="text-sm text-[#5B6675] mb-4">Gesprekken zonder lead of met meerdere passende leads. Koppel ze hier handmatig.</p>
      {queue.length === 0 ? <p className="text-sm text-[#5B6675]" data-testid="call-queue-empty">Geen gesprekken in de wachtrij.</p>
        : <div className="space-y-2">{queue.map((c) => <QueueRow key={c.id} c={c} leads={leads} onDone={load} />)}</div>}
    </div>
  );
}

export function RinkelPanel() {
  const [status, setStatus] = useState(null);
  const [agentsKey, setAgentsKey] = useState(0);
  const refresh = () => getRinkelStatus().then(setStatus).catch(() => setStatus({ configured: false }));
  useEffect(() => { refresh(); }, []);
  return (
    <div className="space-y-6" data-testid="rinkel-panel">
      <StatusCard status={status} onRefresh={refresh} />
      {status?.api_ok && <OutboundNumber onSaved={() => setAgentsKey((k) => k + 1)} />}
      {status?.api_ok && <Agents reloadKey={agentsKey} />}
      <WebhookRegister status={status} onDone={refresh} />
      <WebhookInstructions />
      <CallQueue onChanged={refresh} />
    </div>
  );
}
